<template>
  <div :class="['mt-6 ml-4 flex space-between space-x-4 justify-between m-2 mr-4 flex-end', isRTL ? 'flex-direction-row' : '']">
    <div :class="[isRTL !== false ? 'text-right' : 'text-left']">
      <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
        {{ t('divisionsPage.title') }}
      </h1>
      <h1 class="text text-gray-900 dark:text-gray-200">
        {{ t('divisionsPage.subtitle') }}
      </h1>
    </div>

    <div class="flex">
      <label class="input input-bordered flex items-center mr-2" style="width:330px">
        <input type="text" v-model="searchValue" class="grow" :placeholder="t('divisionsPage.searchPlaceholder')" />
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
          <path fill-rule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clip-rule="evenodd" />
        </svg>
      </label>
      
      <button class="btn" @click="openDivisionModal()" style="background-color:#e9e9e9;">{{ t('divisionsPage.addNewDivisionButton') }}</button>
    </div>
  </div>

  <div class="mt-6 px-4">
    <ThemeOnly theme="modern" :invert="true">
      <div
        class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex"
        style="border-radius: 10px; width:98%;height: 70vh;">
        <EasyDataTable :headers="headers" :items="divisions" buttons-pagination :search-field="searchFields"
    :search-value="searchValue">
      <template #item-agents="{ agents }">
        <div class="flex flex-wrap space-x-2 space-y-2">
          <span v-for="agent in agents" :key="agent.agentId" class="px-2 py-1 text-sm rounded-full bg-blue-100 text-blue-800">
            {{ agent.agentName }}
          </span>
        </div>
      </template>
      <template #item-users="{ users }">
        <div class="flex flex-wrap space-x-2 space-y-2">
          <span v-for="user in users" :key="user.userId" class="px-2 py-1 text-sm rounded-full bg-purple-100 text-purple-800">
            {{ user.userName }}
          </span>
        </div>
      </template>
      <template #item-options="{ divisionId, divisionName, agents, users }">
        <button @click="editDivision(divisionId, divisionName, agents, users)" class="mx-2 hover:text-blue-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('divisionsPage.editButton') }}
        </button>
        <button @click="openDeleteModal(divisionId)" class="mx-2 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          {{ t('divisionsPage.deleteButton') }}
        </button>
      </template>
      <template #loading> 
        <ModernLoader />
      </template>
    </EasyDataTable>
      </div>
    </ThemeOnly>
  </div>

  <it-modal v-model="isDeleteModalOpen">
    <div class="p-4">
      <h2 class="text-2xl font-bold mb-4">{{ t('divisionsPage.deleteModalTitle') }}</h2>
      {{ t('divisionsPage.deleteModalConfirmText') }}

      <div class="flex justify-end mt-6">
        <button @click="isDeleteModalOpen=false" type="button" class="mr-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
          {{ t('divisionsPage.cancelButton') }}
        </button>
        <button @click="deleteDivision()" type="submit" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
          {{ t('divisionsPage.confirmDeleteButton') }}
        </button>
      </div>
    </div>
  </it-modal>

  <it-modal v-model="isDivisionModalOpen">
    <div class="flex items-center justify-center px-4">
      <div class="p-1s max-w-2xl w-full" style="z-index:1000">
        <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-gray-100"
              :class="{ 'text-right dir-rtl': isRTL === true }">
              {{ isEditingDivision !== false ? t('divisionsPage.divisionModalEditTitle') : t('divisionsPage.divisionModalAddTitle') }}
            </h3>
            <div class="flex items-center space-x-2">
              <span class="px-3 py-1 text-sm font-medium rounded-full" 
                :class="isEditingDivision !== false ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'">
                {{ isEditingDivision !== false ? t('divisionsPage.editModeBadge') : t('divisionsPage.createModeBadge') }}
              </span>
            </div>
          </div>
          
          <div class="space-y-6">
            <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                :class="{ 'text-right dir-rtl': isRTL === true }">
                {{ t('divisionsPage.divisionNameLabel') }}
              </label>
              <input v-model="divisionForm.divisionName" 
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white" 
                type="text" 
                :placeholder="t('divisionsPage.divisionNamePlaceholder')">
            </div>
            
            <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                :class="{ 'text-right dir-rtl': isRTL === true }">
                {{ t('divisionsPage.availableAiAgentsLabel') }}
              </label>
              <div class="max-h-60 overflow-y-auto space-y-2">
                <div v-for="agent in availableAgents" :key="agent.agentId" 
                  class="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors">
                  <input type="checkbox" 
                    :id="'agent-' + agent.agentId"
                    v-model="divisionForm.agents"
                    :value="agent.agentId"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                  <label :for="'agent-' + agent.agentId" 
                    class="ml-3 block text-sm text-gray-900 dark:text-gray-300">
                    {{ agent.agentName }}
                  </label>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                :class="{ 'text-right dir-rtl': isRTL === true }">
                {{ t('divisionsPage.chooseUsersLabel') }}
              </label>
              <div class="max-h-60 overflow-y-auto space-y-2">
                <div v-for="user in availableUsers" :key="user.userId" 
                  class="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors">
                  <input type="checkbox" 
                    :id="'user-' + user.userId"
                    v-model="divisionForm.users"
                    :value="user.userId"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded">
                  <label :for="'user-' + user.userId" 
                    class="ml-3 block text-sm text-gray-900 dark:text-gray-300">
                    {{ user.userName }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-700">
          <button @click="isEditingDivision !== false ? updateDivision() : saveDivision()" 
            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            {{ isEditingDivision !== false ? t('divisionsPage.updateDivisionButton') : t('divisionsPage.createDivisionButton') }}
          </button>
          <button @click="closeDivisionModal()" 
            class="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500">
            {{ t('divisionsPage.cancelButton') }}
          </button>
        </div>
      </div>
    </div>
  </it-modal>
</template>
 
<script setup lang="js">
import { ref, onMounted, reactive, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import ModernLoader from '@/components/ModernLoader.vue';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import ThemeOnly from '@/components/ThemeOnly.vue';

const { t, currentLanguage } = useI18n();
const toast = useToast();
const router = useRouter();
const isDivisionModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const searchFields = ["divisionName"];
const searchValue = ref("");
const isEditingDivision = ref(false);
const selectedDivisionId = ref(null);

const availableAgents = ref([]);
const availableUsers = ref([]);

const divisionForm = reactive({
  divisionName: '',
  agents: [],
  users: []
});

const headers = [
  { text: t('divisionsPage.tableHeaders.divisionId'), value: "divisionId", sortable: true },
  { text: t('divisionsPage.tableHeaders.divisionName'), value: "divisionName", sortable: true },
  { text: t('divisionsPage.tableHeaders.aiAgents'), value: "agents" },
  { text: t('divisionsPage.tableHeaders.users'), value: "users" },
  { text: t('divisionsPage.tableHeaders.createdAt'), value: "createdAt", sortable: true },
  { text: t('divisionsPage.tableHeaders.updatedAt'), value: "updatedAt", sortable: true },
  { text: t('divisionsPage.tableHeaders.actions'), value: "options" }
];

const divisions = ref([]);

const isRTL = computed(() => currentLanguage.value === 'he');

function getAuthToken() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (isDefinedAndNotEmpty(token) === false) {
    return '';
  }
  return token !== undefined && token !== null ? token : '';
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function loadDivisions() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/divisions/getAllDivisions`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      divisions.value = data.map(division => ({
        divisionId: division.divisionId !== undefined && division.divisionId !== null ? division.divisionId : division.divisionid,
        divisionName: division.divisionName !== undefined && division.divisionName !== null ? division.divisionName : division.divisionname,
        createdAt: new Date(division.createdAt !== undefined && division.createdAt !== null ? division.createdAt : division.createdat).toLocaleString(),
        updatedAt: new Date(division.updatedAt !== undefined && division.updatedAt !== null ? division.updatedAt : division.updatedat).toLocaleString(),
        agents: isDefinedAndNotEmpty(division.agents) != false ? division.agents.map(agent => ({
          agentId: agent.agentId !== undefined && agent.agentId !== null ? agent.agentId : agent.agentid,
          agentName: agent.agentName !== undefined && agent.agentName !== null ? agent.agentName : agent.agentname
        })) : [],
        users: isDefinedAndNotEmpty(division.users) != false ? division.users.map(user => ({
          userId: user.userId !== undefined && user.userId !== null ? user.userId : user.userid,
          userName: user.userName !== undefined && user.userName !== null ? user.userName : user.username
        })) : []
      }));
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? error.reason : t('divisionsPage.toasts.loadDivisionsError'));
    }
  } catch (error) {
    toast.error(t('divisionsPage.toasts.loadDivisionsErrorPrefix') + error.message);
  }
}

async function loadAvailableAgents() {
  try {
    const data = await getAllAIChats({ includeStyles: false });
    const items = Array.isArray(data) === true ? data : [];
    availableAgents.value = items
      .filter(agent => {
        const typeValue = typeof agent?.agenttype === 'string'
          ? agent.agenttype
          : (typeof agent?.agentType === 'string' ? agent.agentType : '');
        if (typeValue === '') {
          return true;
        }
        return typeValue.toLowerCase() === 'privateagent';
      })
      .map(agent => {
        const agentId = agent?.agentId !== undefined && agent?.agentId !== null ? agent.agentId : agent?.agentid;
        const agentName = agent?.agentName !== undefined && agent?.agentName !== null ? agent.agentName : agent?.agentname;
        return {
          agentId,
          agentName
        };
      })
      .filter(agent => agent.agentId !== null && agent.agentId !== undefined);
  } catch (error) {
    const msg = typeof error?.response?.data?.reason === 'string' ? error.response.data.reason : (typeof error?.message === 'string' ? error.message : '');
    toast.error(t('divisionsPage.toasts.loadAgentsErrorPrefix') + msg);
  }
}

async function loadAvailableUsers() {
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/users`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (response.ok === true) {
      const data = await response.json();
      const usersData = Array.isArray(data.users) != false ? data.users : [];
      availableUsers.value = usersData
        .map(user => {
          const userId = user?.userId !== undefined && user?.userId !== null ? user.userId : user?.userid;
          const userName = user?.userName !== undefined && user?.userName !== null ? user.userName : user?.username;
          return {
            userId,
            userName
          };
        })
        .filter(user => user.userId !== null && user.userId !== undefined);
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? error.reason : t('divisionsPage.toasts.loadUsersError'));
    }
  } catch (error) {
    toast.error(t('divisionsPage.toasts.loadUsersErrorPrefix') + error.message);
  }
}

async function saveDivision() {
  if (divisionForm.divisionName.trim() === '') {
    toast.error(t('divisionsPage.toasts.emptyDivisionNameError'));
    return;
  }

  try {
    const filteredAgents = Array.isArray(divisionForm.agents) === true ? divisionForm.agents.filter(id => id !== null && id !== undefined) : [];
    const filteredUsers = Array.isArray(divisionForm.users) === true ? divisionForm.users.filter(id => id !== null && id !== undefined) : [];
    const response = await fetch(`${globalState.apiEndpoint}/api/divisions/createDivision`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        divisionName: divisionForm.divisionName.trim(),
        agents: filteredAgents,
        users: filteredUsers
      })
    });
    
    if (response.ok === true) {
      toast.success(t('divisionsPage.toasts.createDivisionSuccess'));
      await loadDivisions();
      closeDivisionModal();
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? error.reason : t('divisionsPage.toasts.createDivisionError')); 
    }
  } catch (error) {
    toast.error(t('divisionsPage.toasts.createDivisionErrorPrefix') + error.message);
  }
}

async function updateDivision() {
  if (divisionForm.divisionName.trim() === '') {
    toast.error(t('divisionsPage.toasts.emptyDivisionNameError'));
    return;
  }

  try {
    const filteredAgents = Array.isArray(divisionForm.agents) === true ? divisionForm.agents.filter(id => id !== null && id !== undefined) : [];
    const filteredUsers = Array.isArray(divisionForm.users) === true ? divisionForm.users.filter(id => id !== null && id !== undefined) : [];
    const response = await fetch(`${globalState.apiEndpoint}/api/divisions/updateDivision/${selectedDivisionId.value}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        divisionName: divisionForm.divisionName.trim(),
        agents: filteredAgents,
        users: filteredUsers
      })
    });
    
    if (response.ok === true) {
      toast.success(t('divisionsPage.toasts.updateDivisionSuccess'));
      await loadDivisions();
      closeDivisionModal();
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? error.reason : t('divisionsPage.toasts.updateDivisionError'));
    }
  } catch (error) {
    toast.error(t('divisionsPage.toasts.updateDivisionErrorPrefix') + error.message);
  }
}

async function deleteDivision() {
  if (selectedDivisionId.value === null) {
    toast.error(t('divisionsPage.toasts.noDivisionSelectedError'));
    return;
  }

  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/divisions/deleteDivision/${selectedDivisionId.value}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    
    if (response.ok === true) {
      toast.success(t('divisionsPage.toasts.deleteDivisionSuccess'));
      await loadDivisions();
      isDeleteModalOpen.value = false;
      selectedDivisionId.value = null;
    } else {
      const error = await response.json();
      toast.error(isDefinedAndNotEmpty(error.reason) != false ? error.reason : t('divisionsPage.toasts.deleteDivisionError'));
    }
  } catch (error) {
    toast.error(t('divisionsPage.toasts.deleteDivisionErrorPrefix') + error.message);
  }
}

async function openDivisionModal() {
  isEditingDivision.value = false;
  divisionForm.divisionName = '';
  divisionForm.agents = [];
  divisionForm.users = [];
  await loadAvailableAgents(); 
  await loadAvailableUsers();
  isDivisionModalOpen.value = true;
}

function closeDivisionModal() {
  isDivisionModalOpen.value = false;
  divisionForm.divisionName = '';
  divisionForm.agents = [];
  divisionForm.users = [];
}

function openDeleteModal(divisionId) {
  if (divisionId !== null) {
    selectedDivisionId.value = divisionId;
    isDeleteModalOpen.value = true;
  } else {
    toast.error(t('divisionsPage.toasts.invalidDivisionSelected'));
  }
}

function editDivision(divisionId, divisionName, agents, users) {
  selectedDivisionId.value = divisionId;
  divisionForm.divisionName = divisionName;
  const agentsList = Array.isArray(agents) === true ? agents : [];
  const usersList = Array.isArray(users) === true ? users : [];
  divisionForm.agents = agentsList
    .map(a => (a !== null && a !== undefined) ? (a.agentId !== undefined && a.agentId !== null ? a.agentId : a.agentid) : undefined)
    .filter(id => id !== null && id !== undefined);
  divisionForm.users = usersList
    .map(u => (u !== null && u !== undefined) ? (u.userId !== undefined && u.userId !== null ? u.userId : u.userid) : undefined)
    .filter(id => id !== null && id !== undefined);
  isEditingDivision.value = true;
  isDivisionModalOpen.value = true;
  loadAvailableAgents();
  loadAvailableUsers();
}

onMounted(() => {
  loadDivisions();
  loadAvailableAgents();
  loadAvailableUsers();
});
</script>

<style>
table {
  font-family: "Heebo", sans-serif !important;
}
</style> 